import { ChangeDetectionStrategy, Component, Input, OnInit } from '@angular/core';
import { faEmptySet, IconDefinition } from '@fortawesome/pro-duotone-svg-icons';
import { TranslateService } from '@ngx-translate/core';
import { Select, Store } from '@ngxs/store';
import { PanelBarItemModel, SelectEvent } from '@progress/kendo-angular-layout';
import _ from 'lodash';
import { combineLatest, Observable, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { BrickCategory } from '../../../samwebDescription/samweb/brickCategoryType';
import { BrickDefinition } from '../../../samwebDescription/samweb/brickDefinition';
import { ITabItem } from '../../../samwebDescription/samweb/ITabItem';
import {
   BrickCategoryTabSelectedAction,
   BrickDescriptionSearchChangedAction,
   BrickDescriptionSelectedForDetailsAction,
} from '../../../store/dashboard/brick-menu/add-new-brick-dialog.actions';
import { AddNewBrickDialogState } from '../../../store/dashboard/brick-menu/add-new-brick-dialog.state';
import { BrickDefinitionState } from '../../../store/dashboard/brick-wall/brick-definition/brick-definition.state';
import { BrickInstancesSelectors } from '../../../store/dashboard/brick-wall/brick-instances/brick-instances.selectors';

@Component({
   selector: 'samweb-add-brick-dialog',
   templateUrl: './add-brick-dialog.component.html',
   styleUrls: ['./add-brick-dialog.component.scss'],
   changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AddBrickDialogComponent implements OnInit {
   @Select(AddNewBrickDialogState.brickDefinitionSearchInput)
   public brickDefinitionSearchInput$: Observable<string>;

   public brickCategoriesTabs$: Observable<ITabItem<BrickCategory>[]>;

   @Select(AddNewBrickDialogState.selectedTabIndex)
   public selectedTabIndex$: Observable<number>;

   @Select(AddNewBrickDialogState.selectedBrickDefinitionForDetails)
   public selectedBrickDefinitionId$: Observable<string>;

   @Input()
   public addingBrickSpecialHandlingByOtherBrickInstanceId: string;

   public noResultsIcon: IconDefinition = faEmptySet;

   public brickDefinitions$: Observable<BrickDefinition[]>;

   constructor(private store: Store, private translateService: TranslateService) {}

   ngOnInit(): void {
      this.brickCategoriesTabs$ = this.store.select(AddNewBrickDialogState.brickCategoriesTabsFn).pipe(map((fn) => fn(this.store)));

      this.brickDefinitions$ = combineLatest([
         this.store.select(AddNewBrickDialogState.brickDefinitionSearchInput),
         this.store.select(AddNewBrickDialogState.selectedCategoryTabFn).pipe(map((fn) => fn(this.store))),
         this.store.select(BrickDefinitionState.brickDefinitions),
         this.store.select(BrickInstancesSelectors.isBrickUsageAllowedFn),
      ]).pipe(
         switchMap(([searchText, selectedTab, brickDefinitions, isBrickUsageAllowedFn]) =>
            of(null).pipe(
               map(() => {
                  let filteredDefinitions = brickDefinitions;

                  filteredDefinitions = _(filteredDefinitions)
                     .filter((brickDefinition) => !brickDefinition.disableAddingByUser && isBrickUsageAllowedFn(brickDefinition))
                     .value();

                  // filter category
                  if (!_.isNil(selectedTab.itemRepresentationValue)) {
                     filteredDefinitions = filteredDefinitions.filter((x) => x.brickCategory === selectedTab.itemRepresentationValue);
                  }

                  // filter for matching search text
                  if (!_.isEmpty(searchText)) {
                     const titleTranslations = this.translateService.instant(filteredDefinitions.map((x) => x.brickTitleTranslationKey));
                     const descriptionTranslations = this.translateService.instant(filteredDefinitions.map((x) => x.brickDescriptionTranslationKey));

                     const searchValues = _.split(searchText, ' ');
                     filteredDefinitions = filteredDefinitions.filter((definition) => {
                        const titleTranslation = titleTranslations[definition.brickTitleTranslationKey];
                        const descriptionTranslation = descriptionTranslations[definition.brickDescriptionTranslationKey];
                        return _(searchValues).every(
                           (criterion) =>
                              (!_.isNil(titleTranslation) && _.includes(titleTranslation.toLowerCase(), criterion.toLowerCase())) ||
                              (!_.isNil(descriptionTranslation) && _.includes(descriptionTranslation.toLowerCase(), criterion.toLowerCase()))
                        );
                     });
                  }

                  return filteredDefinitions;
               })
            )
         )
      );
   }

   onTabSelected($event: SelectEvent) {
      this.store.dispatch(new BrickCategoryTabSelectedAction($event.index));
   }

   searchTextChanged(value: string) {
      this.store.dispatch(new BrickDescriptionSearchChangedAction(value));
   }

   panelBarSelectionOccurred(data: PanelBarItemModel[]) {
      const selectedItem = _(data)
         .filter((x) => x.expanded)
         .head();
      if (_.isNil(selectedItem)) {
         this.store.dispatch(new BrickDescriptionSelectedForDetailsAction(null));
      } else {
         this.store.dispatch(new BrickDescriptionSelectedForDetailsAction(selectedItem.id));
      }
   }

   public numberOfBricksOnWallForBrickDefinition$(brickDefinitionId: string): Observable<number> {
      return this.store.select(BrickInstancesSelectors.numberOfBricksOnWallForBrickDefinition(brickDefinitionId));
   }
}
